import React, { useState, useEffect, useRef } from 'react';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Mail, Shield, Settings, Clock, Users, Gift, Ban,
  ChevronRight, Check, AlertCircle, Copy, Phone, Upload, File,
  X, CheckCircle, Loader2, Eye, EyeOff
} from 'lucide-react';
import SEO from '../components/SEO';
import { profileApi, PlayerProfile, KycDocument } from '../services/api';

type TabType = 'genel' | 'dogrulama' | 'guvenlik' | 'tercihler' | 'oturumlar' | 'yoksayilan' | 'teklifler' | 'hariç-tutma';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { currentPlayer, wallet, isLoggedIn, refreshPlayerData } = useSarLabAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('genel');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile state
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [documents, setDocuments] = useState<KycDocument[]>([]);

  // Form states
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [kycForm, setKycForm] = useState({
    firstName: '',
    lastName: '',
    tcKimlikNo: '',
    dateOfBirth: '',
    country: 'TR',
    address: '',
    city: '',
    postalCode: '',
    occupation: '',
  });

  // Document upload
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string>('identity_front');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Copy state
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    loadProfileData();
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (currentPlayer) {
      setKycForm(prev => ({
        ...prev,
        firstName: currentPlayer.firstName || '',
        lastName: currentPlayer.lastName || '',
        tcKimlikNo: currentPlayer.tcKimlikNo || '',
        dateOfBirth: currentPlayer.dateOfBirth || '',
      }));
    }
  }, [currentPlayer]);

  const loadProfileData = async () => {
    try {
      const result = await profileApi.getFullProfile();
      if (result.success && result.data) {
        setProfile(result.data.profile);
        setDocuments(result.data.documents);
        if (result.data.profile) {
          setKycForm(prev => ({
            ...prev,
            country: result.data!.profile?.country || 'TR',
            address: result.data!.profile?.address || '',
            city: result.data!.profile?.city || '',
            postalCode: result.data!.profile?.postalCode || '',
            occupation: result.data!.profile?.occupation || '',
          }));
        }
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleKycSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const result = await profileApi.updateKycInfo({
        country: kycForm.country,
        address: kycForm.address,
        city: kycForm.city,
        postalCode: kycForm.postalCode,
        occupation: kycForm.occupation,
      });

      if (result.success) {
        setSuccess(t('profile:messages.infoSaved'));
        await loadProfileData();
        await refreshPlayerData();
      } else {
        setError(result.error || t('common:messages.errorOccurred'));
      }
    } catch (err) {
      setError(t('common:messages.saveError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Sadece PNG, JPG ve PDF dosyaları kabul edilir');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Dosya boyutu 10MB\'dan büyük olamaz');
      return;
    }

    setUploadingDoc(true);
    setError('');

    try {
      const result = await profileApi.uploadKycDocument(file, selectedDocType);
      if (result.success) {
        setSuccess(t('profile:messages.documentUploaded'));
        await loadProfileData();
      } else {
        setError(result.error || t('profile:messages.uploadError'));
      }
    } catch (err) {
      setError(t('common:messages.uploadError'));
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const tabs = [
    { id: 'genel' as TabType, label: t('profile:tabs.general'), icon: User },
    { id: 'dogrulama' as TabType, label: t('profile:tabs.verification'), icon: Shield },
    { id: 'guvenlik' as TabType, label: t('profile:tabs.security'), icon: Settings },
    { id: 'tercihler' as TabType, label: t('profile:tabs.preferences'), icon: Settings },
    { id: 'oturumlar' as TabType, label: t('profile:tabs.sessions'), icon: Clock },
    { id: 'yoksayilan' as TabType, label: t('profile:tabs.blocked'), icon: Users },
    { id: 'teklifler' as TabType, label: t('profile:tabs.offers'), icon: Gift },
    { id: 'hariç-tutma' as TabType, label: t('profile:tabs.selfExclusion'), icon: Ban },
  ];

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'identity_front': 'Kimlik Kartı (Ön Yüz)',
      'identity_back': 'Kimlik Kartı (Arka Yüz)',
      'passport': 'Pasaport',
      'drivers_license': 'Ehliyet',
      'proof_of_address': 'İkametgah Belgesi',
      'selfie': 'Selfie',
      'other': 'Diğer',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400">Beklemede</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">Onaylandı</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">Reddedildi</span>;
      default:
        return null;
    }
  };

  if (!currentPlayer) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Ayarlar - Profil"
        description="Hesap ayarlarınızı yönetin"
      />

      <div className="min-h-screen bg-dark-900 pt-20 pb-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">AYARLAR</h1>
          </div>

          {/* Messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{error}</span>
                <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3"
              >
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400">{success}</span>
                <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-300">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar - Tab Menu */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-2xl p-4">
                <nav className="space-y-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left ${
                        activeTab === tab.id
                          ? 'bg-primary-500/20 text-primary-400 border-l-2 border-primary-500'
                          : 'text-gray-400 hover:bg-dark-700/50 hover:text-white'
                      }`}
                    >
                      <ChevronRight className={`w-4 h-4 transition-transform ${activeTab === tab.id ? 'rotate-90' : ''}`} />
                      <span className="text-sm">{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-dark-800/50 backdrop-blur-md border border-dark-700/50 rounded-2xl p-6"
              >
                {/* GENEL TAB */}
                {activeTab === 'genel' && (
                  <div className="space-y-8">
                    {/* E-posta Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-xl font-bold text-white">E-posta</h2>
                        {currentPlayer.emailVerified && (
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Tamamlandı</span>
                        )}
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">E-posta *</label>
                          <input
                            type="email"
                            value={currentPlayer.email}
                            disabled
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          />
                        </div>
                        <div className="flex justify-end">
                          <button className="px-6 py-2 bg-primary-500/20 text-primary-400 rounded-lg hover:bg-primary-500/30 transition-colors">
                            KAYDET
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Kullanıcı Adı Section */}
                    <div>
                      <h2 className="text-xl font-bold text-white mb-4">Kullanıcı Adı</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Kullanıcı Adı *</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={currentPlayer.username}
                              disabled
                              className="flex-1 px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                            />
                            <button
                              onClick={() => copyToClipboard(currentPlayer.username, 'username')}
                              className="p-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-gray-400 hover:text-white transition-colors"
                            >
                              {copiedField === 'username' ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">ID</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={currentPlayer.id.slice(0, 8)}
                              disabled
                              className="flex-1 px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white font-mono"
                            />
                            <button
                              onClick={() => copyToClipboard(currentPlayer.id, 'id')}
                              className="p-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-gray-400 hover:text-white transition-colors"
                            >
                              {copiedField === 'id' ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Telefon Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-xl font-bold text-white">Telefon</h2>
                        {currentPlayer.phoneVerified ? (
                          <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Doğrulanmış</span>
                        ) : (
                          <span className="text-xs text-yellow-400 bg-yellow-500/20 px-2 py-1 rounded">Doğrulanmamış</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Ülke Kodu *</label>
                          <select className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white">
                            <option value="+90">+(90) Türkiye</option>
                            <option value="+49">+(49) Deutschland</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Telefon Numarası *</label>
                          <input
                            type="tel"
                            value={currentPlayer.phone || ''}
                            disabled
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <button className="px-6 py-2 bg-primary-500/20 text-primary-400 rounded-lg hover:bg-primary-500/30 transition-colors">
                          GÜNCELLE
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* DOGRULAMA TAB */}
                {activeTab === 'dogrulama' && (
                  <div className="space-y-8">
                    {/* KYC Info Form */}
                    <div>
                      <h2 className="text-xl font-bold text-white mb-4">Kimlik Doğrulama Bilgileri</h2>
                      <p className="text-gray-400 text-sm mb-6">
                        Hesabınızı doğrulamak için aşağıdaki bilgileri eksiksiz doldurun.
                      </p>

                      <form onSubmit={handleKycSubmit} className="space-y-6">
                        {/* Personal Info - Read Only */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">İsim *</label>
                            <input
                              type="text"
                              value={kycForm.firstName}
                              disabled
                              className="w-full px-4 py-3 bg-dark-700/30 border border-dark-600/50 rounded-lg text-gray-400"
                            />
                            <p className="text-xs text-gray-500 mt-1">Kayıt sırasında girildi</p>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Soyisim *</label>
                            <input
                              type="text"
                              value={kycForm.lastName}
                              disabled
                              className="w-full px-4 py-3 bg-dark-700/30 border border-dark-600/50 rounded-lg text-gray-400"
                            />
                            <p className="text-xs text-gray-500 mt-1">Kayıt sırasında girildi</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">TC Kimlik No *</label>
                            <input
                              type="text"
                              value={kycForm.tcKimlikNo}
                              disabled
                              className="w-full px-4 py-3 bg-dark-700/30 border border-dark-600/50 rounded-lg text-gray-400"
                            />
                            <p className="text-xs text-gray-500 mt-1">Kayıt sırasında girildi</p>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-2">Doğum Tarihi *</label>
                            <input
                              type="text"
                              value={kycForm.dateOfBirth}
                              disabled
                              className="w-full px-4 py-3 bg-dark-700/30 border border-dark-600/50 rounded-lg text-gray-400"
                            />
                            <p className="text-xs text-gray-500 mt-1">Kayıt sırasında girildi</p>
                          </div>
                        </div>

                        {/* Editable Fields */}
                        <div className="border-t border-dark-700/50 pt-6">
                          <h3 className="text-lg font-semibold text-white mb-4">Ek Bilgiler</h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Ülke *</label>
                              <select
                                value={kycForm.country}
                                onChange={(e) => setKycForm(prev => ({ ...prev, country: e.target.value }))}
                                className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                              >
                                <option value="TR">Türkiye</option>
                                <option value="DE">Almanya</option>
                                <option value="NL">Hollanda</option>
                                <option value="GB">İngiltere</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Şehir *</label>
                              <input
                                type="text"
                                value={kycForm.city}
                                onChange={(e) => setKycForm(prev => ({ ...prev, city: e.target.value }))}
                                placeholder="İstanbul"
                                className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-500"
                              />
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm text-gray-400 mb-2">İkamet Adresi *</label>
                            <input
                              type="text"
                              value={kycForm.address}
                              onChange={(e) => setKycForm(prev => ({ ...prev, address: e.target.value }))}
                              placeholder="Mahalle, Sokak, Bina No, Daire No"
                              className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-500"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Posta Kodu</label>
                              <input
                                type="text"
                                value={kycForm.postalCode}
                                onChange={(e) => setKycForm(prev => ({ ...prev, postalCode: e.target.value }))}
                                placeholder="34000"
                                className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Meslek</label>
                              <input
                                type="text"
                                value={kycForm.occupation}
                                onChange={(e) => setKycForm(prev => ({ ...prev, occupation: e.target.value }))}
                                placeholder="Yazılım Mühendisi"
                                className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-500"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-8 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                            GÖNDER
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Document Upload Section */}
                    <div className="border-t border-dark-700/50 pt-8">
                      <h2 className="text-xl font-bold text-white mb-4">Belge Yükleme</h2>
                      <p className="text-gray-400 text-sm mb-6">
                        Kimlik kartınızın ön ve arka yüzünü yükleyin. Kabul edilen dosya türleri: PNG, JPG, PDF (max 10MB)
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Belge Türü</label>
                          <select
                            value={selectedDocType}
                            onChange={(e) => setSelectedDocType(e.target.value)}
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          >
                            <option value="identity_front">Kimlik Kartı (Ön Yüz)</option>
                            <option value="identity_back">Kimlik Kartı (Arka Yüz)</option>
                            <option value="passport">Pasaport</option>
                            <option value="drivers_license">Ehliyet</option>
                            <option value="proof_of_address">İkametgah Belgesi</option>
                            <option value="selfie">Selfie (Kimlikle)</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingDoc}
                            className="w-full px-6 py-3 bg-dark-700/50 border border-dashed border-dark-500 rounded-lg text-gray-400 hover:border-primary-500 hover:text-primary-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {uploadingDoc ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Upload className="w-5 h-5" />
                            )}
                            {uploadingDoc ? 'Yükleniyor...' : 'Dosya Seç'}
                          </button>
                        </div>
                      </div>

                      {/* Uploaded Documents */}
                      {documents.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-4">Yüklenen Belgeler</h3>
                          <div className="space-y-3">
                            {documents.map((doc) => (
                              <div
                                key={doc.id}
                                className="flex items-center justify-between p-4 bg-dark-700/30 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <File className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <p className="text-white text-sm">{getDocumentTypeLabel(doc.documentType)}</p>
                                    <p className="text-gray-500 text-xs">{doc.fileName}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  {getStatusBadge(doc.status)}
                                  {doc.rejectionReason && (
                                    <span className="text-xs text-red-400">{doc.rejectionReason}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* GUVENLIK TAB */}
                {activeTab === 'guvenlik' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Güvenlik Ayarları</h2>
                    <div className="bg-dark-700/30 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Şifre Değiştir</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Mevcut Şifre</label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Yeni Şifre</label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Yeni Şifre (Tekrar)</label>
                          <input
                            type="password"
                            className="w-full px-4 py-3 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white"
                          />
                        </div>
                        <button className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
                          Şifreyi Güncelle
                        </button>
                      </div>
                    </div>

                    <div className="bg-dark-700/30 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">İki Faktörlü Doğrulama</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Hesabınızı daha güvenli hale getirmek için iki faktörlü doğrulamayı aktifleştirin.
                      </p>
                      <button className="px-6 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-500 transition-colors">
                        2FA Aktifleştir
                      </button>
                    </div>
                  </div>
                )}

                {/* TERCIHLER TAB */}
                {activeTab === 'tercihler' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Tercihler</h2>
                    <div className="bg-dark-700/30 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Bildirim Ayarları</h3>
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" defaultChecked className="w-5 h-5 rounded bg-dark-600 border-dark-500" />
                          <span className="text-gray-300">E-posta bildirimleri</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" defaultChecked className="w-5 h-5 rounded bg-dark-600 border-dark-500" />
                          <span className="text-gray-300">Promosyon bildirimleri</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" className="w-5 h-5 rounded bg-dark-600 border-dark-500" />
                          <span className="text-gray-300">SMS bildirimleri</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* OTURUMLAR TAB */}
                {activeTab === 'oturumlar' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Aktif Oturumlar</h2>
                    <div className="bg-dark-700/30 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <p className="text-white">Mevcut Oturum</p>
                            <p className="text-gray-500 text-sm">Bu cihaz</p>
                          </div>
                        </div>
                        <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Aktif</span>
                      </div>
                      <button className="px-6 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors">
                        Tüm Oturumları Sonlandır
                      </button>
                    </div>
                  </div>
                )}

                {/* YOKSAYILAN TAB */}
                {activeTab === 'yoksayilan' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Yoksayılan Kullanıcılar</h2>
                    <div className="bg-dark-700/30 rounded-lg p-6 text-center">
                      <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">Henüz yoksayılan kullanıcı yok</p>
                    </div>
                  </div>
                )}

                {/* TEKLIFLER TAB */}
                {activeTab === 'teklifler' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Teklifler</h2>
                    <div className="bg-dark-700/30 rounded-lg p-6">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" defaultChecked className="w-5 h-5 rounded bg-dark-600 border-dark-500" />
                        <span className="text-gray-300">Promosyon tekliflerini almak istiyorum</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* HARIÇ TUTMA TAB */}
                {activeTab === 'hariç-tutma' && (
                  <div className="space-y-8">
                    <h2 className="text-xl font-bold text-white mb-4">Kendi Kendini Hariç Tutma</h2>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-yellow-400 mb-2">Uyarı</h3>
                          <p className="text-gray-300 text-sm mb-4">
                            Bu özellik, belirli bir süre için hesabınızı devre dışı bırakmanızı sağlar.
                            Bu süre zarfında giriş yapamaz ve oyun oynayamazsınız.
                          </p>
                          <div className="space-y-3">
                            <button className="w-full px-6 py-3 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors text-left">
                              24 Saat Ara Ver
                            </button>
                            <button className="w-full px-6 py-3 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors text-left">
                              1 Hafta Ara Ver
                            </button>
                            <button className="w-full px-6 py-3 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors text-left">
                              1 Ay Ara Ver
                            </button>
                            <button className="w-full px-6 py-3 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-left">
                              Hesabı Kalıcı Olarak Kapat
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
